/**
 * Behind / on-schedule / ahead computation for the crew Today screen.
 *
 * Pure + side-effect-free so it's unit-testable and SSR-safe. The crew
 * card used to hard-code "on schedule" (CrewToday.tsx:138) — a quiet lie
 * the moment a job ran long. This derives an honest, current-moment read
 * from the schedule the day already carries.
 *
 * Signals (most-late wins for `behind`):
 *  - an active task (en route / on site / in progress / paused) that has
 *    run past its scheduled end, and
 *  - a not-yet-started task whose scheduled start is already in the past.
 *
 * `ahead` is asserted conservatively — only when the active task got an
 * early start and is still inside its planned window — so we never
 * over-claim. Everything else is `on_schedule`; an all-complete day is
 * `done`; an empty day is `idle`.
 */
import type { CrewTodayTask } from '@vari-crew/schemas';

export type CrewScheduleState = 'idle' | 'on_schedule' | 'behind' | 'ahead' | 'done';

export interface CrewScheduleStatus {
  state: CrewScheduleState;
  /** Whole minutes behind (state==='behind') or ahead (state==='ahead'); 0 otherwise. */
  minutes: number;
}

/** The fields this computation needs — a structural subset of CrewTodayTask. */
export type ScheduleTask = Pick<
  CrewTodayTask,
  'status' | 'scheduled_start' | 'scheduled_end' | 'started_at' | 'completed_at'
>;

const ACTIVE_STATUSES: ReadonlySet<string> = new Set([
  'en_route',
  'on_site',
  'in_progress',
  'paused',
]);

/** Below this we treat lateness as schedule noise rather than "behind". */
const BEHIND_GRACE_MIN = 1;
/** Ahead is a softer claim, so require a clearer margin before asserting it. */
const AHEAD_GRACE_MIN = 5;

function ms(iso: string | null): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : null;
}

export function computeCrewScheduleStatus(
  tasks: readonly ScheduleTask[],
  now: Date = new Date(),
): CrewScheduleStatus {
  const live = tasks.filter((t) => t.status !== 'cancelled');
  if (live.length === 0) return { state: 'idle', minutes: 0 };

  const nowMs = now.getTime();

  // --- behind: take the largest lateness across both signals ---
  let behindMs = 0;
  for (const t of live) {
    if (ACTIVE_STATUSES.has(t.status)) {
      const end = ms(t.scheduled_end);
      if (end != null && nowMs > end) behindMs = Math.max(behindMs, nowMs - end);
    }
    if (t.status === 'scheduled') {
      const start = ms(t.scheduled_start);
      if (start != null && nowMs > start) behindMs = Math.max(behindMs, nowMs - start);
    }
  }
  const behindMin = Math.round(behindMs / 60_000);
  if (behindMin >= BEHIND_GRACE_MIN) return { state: 'behind', minutes: behindMin };

  if (live.every((t) => t.status === 'complete')) return { state: 'done', minutes: 0 };

  // --- ahead: active task started early and is still within its window ---
  let aheadMs = 0;
  for (const t of live) {
    if (!ACTIVE_STATUSES.has(t.status)) continue;
    const start = ms(t.scheduled_start);
    const started = ms(t.started_at);
    const end = ms(t.scheduled_end);
    if (start != null && started != null && started < start && (end == null || nowMs <= end)) {
      aheadMs = Math.max(aheadMs, start - started);
    }
  }
  const aheadMin = Math.round(aheadMs / 60_000);
  if (aheadMin >= AHEAD_GRACE_MIN) return { state: 'ahead', minutes: aheadMin };

  return { state: 'on_schedule', minutes: 0 };
}
